import { useEffect, useState, useMemo } from "react";
import { api } from "@/lib/api";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Textarea } from "@/components/ui/textarea";
import { Badge } from "@/components/ui/badge";
import { toast } from "sonner";
import { PaperPlaneTilt, Envelope, EnvelopeOpen, Trash, ArrowClockwise, ChatCircleDots } from "@phosphor-icons/react";

function fmtWhen(iso) {
  if (!iso) return "";
  try {
    const d = new Date(iso);
    const now = new Date();
    const sameDay = d.toDateString() === now.toDateString();
    if (sameDay) return d.toLocaleTimeString("de-DE", { hour: "2-digit", minute: "2-digit" });
    const yest = new Date(now); yest.setDate(now.getDate() - 1);
    if (d.toDateString() === yest.toDateString())
      return "Gestern " + d.toLocaleTimeString("de-DE", { hour: "2-digit", minute: "2-digit" });
    return d.toLocaleDateString("de-DE", { day: "2-digit", month: "2-digit", year: "numeric" }) +
      " · " + d.toLocaleTimeString("de-DE", { hour: "2-digit", minute: "2-digit" });
  } catch { return iso; }
}

export default function WhatsAppInbox() {
  const [items, setItems] = useState([]);
  const [unread, setUnread] = useState(0);
  const [loading, setLoading] = useState(true);
  const [selectedId, setSelectedId] = useState(null);
  const [reply, setReply] = useState("");
  const [sending, setSending] = useState(false);
  const [unreadOnly, setUnreadOnly] = useState(false);

  const load = async () => {
    setLoading(true);
    try {
      const { data } = await api.get(`/whatsapp/inbox?unread_only=${unreadOnly}&limit=200`);
      setItems(data.items || []);
      setUnread(data.unread || 0);
      if (!selectedId && (data.items || []).length) setSelectedId(data.items[0].id);
    } catch (e) {
      toast.error(e.response?.data?.detail || "Inbox konnte nicht geladen werden");
    } finally { setLoading(false); }
  };

  useEffect(() => { load(); /* eslint-disable-next-line */ }, [unreadOnly]);
  useEffect(() => {
    const t = setInterval(load, 30000); // Auto-Refresh alle 30 s
    return () => clearInterval(t);
    // eslint-disable-next-line
  }, [unreadOnly]);

  const selected = useMemo(
    () => items.find(x => x.id === selectedId) || null,
    [items, selectedId]
  );

  const markRead = async (id) => {
    try {
      await api.post(`/whatsapp/inbox/${id}/read`);
      setItems(list => list.map(x => x.id === id ? { ...x, read: true } : x));
      setUnread(n => Math.max(0, n - 1));
    } catch {}
  };

  const onSelect = (id) => {
    setSelectedId(id);
    setReply("");
    const it = items.find(x => x.id === id);
    if (it && !it.read) markRead(id);
  };

  const sendReply = async () => {
    if (!selected) return;
    const txt = reply.trim();
    if (!txt) { toast.error("Antworttext fehlt"); return; }
    setSending(true);
    try {
      await api.post(`/whatsapp/inbox/${selected.id}/reply`, { text: txt });
      toast.success("Antwort gesendet");
      setReply("");
      await load();
    } catch (e) {
      toast.error(e.response?.data?.detail || "Versand fehlgeschlagen");
    } finally { setSending(false); }
  };

  const onDelete = async (id) => {
    if (!window.confirm("Nachricht endgültig löschen?")) return;
    try {
      await api.delete(`/whatsapp/inbox/${id}`);
      setItems(list => list.filter(x => x.id !== id));
      if (selectedId === id) setSelectedId(null);
      toast.success("Gelöscht");
    } catch (e) {
      toast.error("Löschen fehlgeschlagen");
    }
  };

  return (
    <div className="space-y-4" data-testid="whatsapp-inbox-page">
      <div className="flex items-center justify-between flex-wrap gap-3">
        <div>
          <h1 className="text-4xl sm:text-5xl font-bold tracking-tight flex items-center gap-3">
            <ChatCircleDots size={40} weight="duotone" className="text-emerald-600"/>
            WhatsApp-Inbox
          </h1>
          <p className="text-sm text-muted-foreground mt-1">
            Eingehende Nachrichten von Gästen · direkt aus dem Cockpit antworten.
          </p>
        </div>
        <div className="flex items-center gap-2">
          {unread > 0 && (
            <Badge className="bg-emerald-600 text-white" data-testid="wa-unread-badge">
              {unread} ungelesen
            </Badge>
          )}
          <Button variant={unreadOnly ? "default" : "outline"} size="sm"
                  onClick={() => setUnreadOnly(v => !v)}
                  data-testid="wa-filter-unread"
                  className="rounded-xl">
            {unreadOnly ? "Alle anzeigen" : "Nur ungelesen"}
          </Button>
          <Button variant="outline" size="sm" onClick={load}
                  data-testid="wa-reload"
                  className="rounded-xl">
            <ArrowClockwise size={16} className="mr-1"/>Neu laden
          </Button>
        </div>
      </div>

      <div className="grid grid-cols-1 lg:grid-cols-[380px_1fr] gap-4">
        {/* Liste */}
        <div className="border border-border rounded-2xl bg-card overflow-hidden">
          <div className="px-4 py-3 border-b border-border bg-muted/30 text-xs uppercase tracking-widest font-semibold text-muted-foreground">
            📥 Konversationen ({items.length})
          </div>
          <div className="max-h-[70vh] overflow-y-auto divide-y divide-border/60">
            {loading && (
              <div className="p-8 text-center text-sm text-muted-foreground">Lade…</div>
            )}
            {!loading && items.length === 0 && (
              <div className="p-8 text-center text-sm text-muted-foreground">
                <ChatCircleDots size={40} className="mx-auto mb-2 opacity-30"/>
                Noch keine WhatsApp-Nachrichten.<br/>
                Sobald ein Gast schreibt, erscheinen die Nachrichten hier.
              </div>
            )}
            {items.map(m => {
              const isSel = m.id === selectedId;
              return (
                <button key={m.id} onClick={() => onSelect(m.id)}
                        data-testid={`wa-item-${m.id}`}
                        className={`w-full text-left px-4 py-3 transition ${
                          isSel ? "bg-primary/10 border-l-4 border-primary"
                                : "hover:bg-muted/40 border-l-4 border-transparent"
                        }`}>
                  <div className="flex items-center gap-2 mb-1">
                    {!m.read
                      ? <Envelope size={16} weight="fill" className="text-emerald-600 shrink-0"/>
                      : <EnvelopeOpen size={16} className="text-muted-foreground shrink-0"/>}
                    <span className={`text-sm truncate ${!m.read ? "font-semibold" : "font-medium"}`}>
                      {m.profile_name || m.guest_name || m.phone || "Unbekannt"}
                    </span>
                    {m.replied && (
                      <Badge variant="outline" className="ml-auto text-[10px] py-0 h-4">geantwortet</Badge>
                    )}
                  </div>
                  <div className="text-xs text-muted-foreground truncate">
                    {m.body || (m.num_media ? `📎 ${m.num_media} Medien` : "(leer)")}
                  </div>
                  <div className="text-[10px] text-muted-foreground mt-1 flex items-center gap-2">
                    <span>{fmtWhen(m.received_at)}</span>
                    <span>·</span>
                    <span className="font-mono">{m.phone}</span>
                    {m.booking_id && <span className="text-primary">· 📋 Buchung</span>}
                  </div>
                </button>
              );
            })}
          </div>
        </div>

        {/* Detail + Antwort */}
        <div className="border border-border rounded-2xl bg-card overflow-hidden flex flex-col min-h-[70vh]">
          {!selected && (
            <div className="flex-1 grid place-items-center text-center p-8 text-muted-foreground">
              <div>
                <ChatCircleDots size={64} className="mx-auto mb-3 opacity-30"/>
                <div className="text-lg font-semibold mb-1">Nachricht auswählen</div>
                <div className="text-sm">Klicke links auf eine Konversation, um sie zu lesen und zu antworten.</div>
              </div>
            </div>
          )}
          {selected && (
            <>
              <div className="px-6 py-4 border-b border-border bg-muted/30 flex items-center justify-between">
                <div>
                  <div className="text-lg font-bold flex items-center gap-2">
                    {selected.profile_name || selected.guest_name || "Unbekannt"}
                    {selected.guest_name && selected.profile_name && (
                      <span className="text-xs font-normal text-muted-foreground">(Gast: {selected.guest_name})</span>
                    )}
                  </div>
                  <div className="text-xs text-muted-foreground font-mono">{selected.phone}</div>
                </div>
                <div className="flex items-center gap-2">
                  <Button variant="ghost" size="sm" onClick={() => onDelete(selected.id)}
                          data-testid={`wa-delete-${selected.id}`}
                          className="text-red-600 hover:bg-red-50">
                    <Trash size={16}/>
                  </Button>
                </div>
              </div>

              <div className="flex-1 overflow-y-auto p-6 space-y-4 bg-[#e5ddd5]/30">
                {/* Gast-Nachricht */}
                <div className="max-w-[70%]">
                  <div className="bg-white rounded-2xl rounded-tl-none px-4 py-3 shadow-sm">
                    <div className="text-sm whitespace-pre-wrap">{selected.body || "(nur Medien)"}</div>
                    {selected.num_media > 0 && (
                      <div className="mt-2 space-y-1">
                        {(selected.media || []).map((m, i) => (
                          <div key={i} className="text-xs text-primary">
                            📎 <a href={m.url} target="_blank" rel="noreferrer" className="underline">
                              Medium {i + 1} ({m.type || "unbekannt"})
                            </a>
                          </div>
                        ))}
                      </div>
                    )}
                  </div>
                  <div className="text-[10px] text-muted-foreground mt-1 ml-2">{fmtWhen(selected.received_at)}</div>
                </div>

                {/* Bereits gesendete Antwort */}
                {selected.replied && selected.last_reply_text && (
                  <div className="max-w-[70%] ml-auto">
                    <div className="bg-emerald-500 text-white rounded-2xl rounded-tr-none px-4 py-3 shadow-sm">
                      <div className="text-sm whitespace-pre-wrap">{selected.last_reply_text}</div>
                    </div>
                    <div className="text-[10px] text-muted-foreground mt-1 mr-2 text-right">
                      Gesendet · {fmtWhen(selected.replied_at)}
                    </div>
                  </div>
                )}
              </div>

              {/* Antwort-Editor */}
              <div className="border-t border-border p-4 bg-muted/20">
                <Textarea
                  value={reply}
                  onChange={e => setReply(e.target.value)}
                  placeholder="Antwort tippen… (Freeform funktioniert innerhalb 24 h nach Gast-Nachricht)"
                  className="rounded-xl min-h-[80px] bg-white"
                  data-testid="wa-reply-input"
                  onKeyDown={e => { if (e.key === "Enter" && (e.metaKey || e.ctrlKey)) sendReply(); }}
                />
                <div className="flex items-center justify-between mt-2">
                  <span className="text-[11px] text-muted-foreground">
                    ⌘/Strg + Enter zum Senden · {reply.length}/1500
                  </span>
                  <Button onClick={sendReply} disabled={sending || !reply.trim()}
                          data-testid="wa-reply-send"
                          className="rounded-xl bg-emerald-600 hover:bg-emerald-700">
                    <PaperPlaneTilt size={16} className="mr-2"/>
                    {sending ? "Sende…" : "Antworten"}
                  </Button>
                </div>
              </div>
            </>
          )}
        </div>
      </div>
    </div>
  );
}
